import React from "react";
import { Input } from "@/components/ui/input";
import { CountryField } from "@/components/CountryField";
import { Label } from "@/components/ui/label";
import { useLang } from "@/i18n";

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const ageOn = (birth, ref) => {
  if (!birth || !ref) return null;
  const b = new Date(birth);
  const r = new Date(ref);
  let a = r.getFullYear() - b.getFullYear();
  const m = r.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && r.getDate() < b.getDate())) a -= 1;
  return a;
};

export const idRequired = (o, arrival) => o.kind === "adult" || (ageOn(o.birth_date, arrival) ?? 0) > 12;

export const occupantMissing = (o, arrival) => {
  const miss = [];
  if (!o.first_name) miss.push("first_name");
  if (!o.last_name) miss.push("last_name");
  if (!o.birth_date) miss.push("birth_date");
  if (idRequired(o, arrival) && !o.passport_number) miss.push("passport_number");
  return miss;
};

export default function OccupantFields({ occupant: o, onChange, arrival, departure, highlightMissing = false, testPrefix }) {
  const { t } = useLang();
  const set = (k) => (e) => onChange({ ...o, [k]: e.target.value });
  const miss = highlightMissing ? new Set(occupantMissing(o, arrival)) : new Set();
  const inv = (k) => (miss.has(k) ? true : undefined);
  const needsId = idRequired(o, arrival);

  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`${testPrefix}-fields`}>
      <F label={t("first_name")}><Input required value={o.first_name} onChange={set("first_name")} data-invalid={inv("first_name")} data-testid={`${testPrefix}-first-name`} /></F>
      <F label={t("last_name")}><Input required value={o.last_name} onChange={set("last_name")} data-invalid={inv("last_name")} data-testid={`${testPrefix}-last-name`} /></F>
      <F label={t("u_birth")}>
        <Input required type="date" max={new Date().toISOString().slice(0, 10)} value={o.birth_date} onChange={set("birth_date")} data-invalid={inv("birth_date")} data-testid={`${testPrefix}-birth`} />
      </F>
      <F label={`${t("passport")}${needsId ? " *" : ""}`}>
        <Input required={needsId} value={o.passport_number} onChange={set("passport_number")} data-invalid={inv("passport_number")} data-testid={`${testPrefix}-passport`} />
      </F>
      {needsId && <p className="col-span-2 text-[11px] text-muted-foreground -mt-1">{t("id_required_over12")}</p>}
      <F label={t("issue_country")}><CountryField value={o.passport_country} onChange={set("passport_country")} testid={`${testPrefix}-passport-country`} /></F>
      <F label={t("issue_place")}><Input value={o.passport_place} onChange={set("passport_place")} data-testid={`${testPrefix}-passport-place`} /></F>

      <p className="col-span-2 mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("same_address_hint")}</p>
      <F label={t("u_street")} className="col-span-2"><Input value={o.street} onChange={set("street")} data-testid={`${testPrefix}-street`} /></F>
      <F label={t("u_house")}><Input value={o.house_number} onChange={set("house_number")} data-testid={`${testPrefix}-house`} /></F>
      <F label={t("u_postal")}><Input value={o.postal_code} onChange={set("postal_code")} data-testid={`${testPrefix}-postal`} /></F>
      <F label={t("u_city")}><Input value={o.city} onChange={set("city")} data-testid={`${testPrefix}-city`} /></F>
      <F label={t("country")}><CountryField value={o.country} onChange={set("country")} testid={`${testPrefix}-country`} /></F>

      <p className="col-span-2 mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("occupant_period")}</p>
      <F label={t("arrival")}>
        <Input type="date" min={arrival} max={departure} value={o.arrival || arrival} onChange={set("arrival")} data-testid={`${testPrefix}-arrival`} />
      </F>
      <F label={t("departure")}>
        <Input type="date" min={o.arrival || arrival} max={departure} value={o.departure || departure} onChange={set("departure")} data-testid={`${testPrefix}-departure`} />
      </F>
    </div>
  );
}
